import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import { IconButton } from '~shared/components/buttons';
import { IconVisibility, IconX } from '~shared/components/icons/standard';
import { Direction, sortItemsByProperty } from '~shared/utils/sort';

import { Participant, StudentResponse } from '../ResultsGrid/ResultsTable';
import { ResultsQuestionAnswerResult } from './ResultsQuestionAnswerResult';

interface Props {
  participants: Participant[];
  questionId: string | number;
  numParticipantsResponded: number;
  showAnswers: boolean;
  showNames: boolean;
  onRemoveResponse?: (responseId: number) => void;
  showRemoveAnswer: boolean;
}

function AnswerText({
  response,
  showAnswer,
  removeResponse = () => {},
  showRemoveAnswer,
  showName,
}: {
  response: StudentResponse;
  removeResponse: (responseId: number) => void;
  showAnswer: boolean;
  showRemoveAnswer: boolean;
  showName: boolean;
}): React.ReactElement {
  const [isVisible, setIsVisible] = React.useState(showAnswer);
  /**
   * Answers can control their own visibility state, but if the
   * higher-level `showAnswer` prop changes, it should respect that
   * setting instead.
   */
  React.useEffect(() => {
    if (showAnswer !== isVisible) {
      setIsVisible(showAnswer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnswer]);

  const resultClass = cx(
    'flex',
    !isVisible && 'tracking-widest',
    !showName && 'col-span-2'
  );

  return (
    <div className={resultClass}>
      <ResultsQuestionAnswerResult>
        {isVisible ? response.answerText : '•••••••'}
      </ResultsQuestionAnswerResult>
      {showRemoveAnswer ? (
        <IconButton
          appearance="ghost"
          isRisky
          withAltBackground
          aria-label={translate('Remove Answer')}
          onClick={() => removeResponse(response.id)}
        >
          <IconX />
        </IconButton>
      ) : (
        <IconButton
          appearance="ghost"
          withAltBackground
          aria-label={
            isVisible ? translate('Hide answer') : translate('Show answer')
          }
          onClick={() => setIsVisible(!isVisible)}
        >
          <IconVisibility hidden={!isVisible} />
        </IconButton>
      )}
    </div>
  );
}

const ResultsQuestionAnswerShortAnswerResponse = ({
  response,
  showNames,
  showAnswers,
  handleRemoveResponse,
  showRemoveAnswer,
  participant,
}: {
  showNames: boolean;
  showAnswers: boolean;
  handleRemoveResponse: (responseId: number) => void;
  showRemoveAnswer: boolean;
  response: StudentResponse;
  participant: Participant;
}) => {
  if (!response.answerText) return null;
  if (response.hidden) return null;

  return (
    <>
      {showNames ? (
        <div className="flex items-center max-w-60 px-4 py-2 bg-grey-50 rounded-1">
          <div className="truncate">
            <span className="font-sans text-sm leading-8 text-blue-900">
              {participant.name}
            </span>
          </div>
        </div>
      ) : null}
      <AnswerText
        response={response}
        showAnswer={showAnswers}
        removeResponse={handleRemoveResponse}
        showRemoveAnswer={showRemoveAnswer}
        showName={showNames}
      />
    </>
  );
};

function sortResponses(
  participants: Participant[] = [],
  questionId: number | string
) {
  const responses = participants.reduce(
    (accumulator, participant) =>
      accumulator.concat(
        participant.responsesByQuestion[questionId].map((response) => ({
          ...response,
          participant: participant,
        }))
      ),
    []
  );

  return sortItemsByProperty({
    items: responses,
    property: 'id',
    sortDirection: Direction.Descending,
  });
}

export function ResultsQuestionAnswersSA({
  participants,
  questionId,
  numParticipantsResponded,
  showAnswers,
  showNames,
  onRemoveResponse = null,
  showRemoveAnswer,
}: Props): React.ReactElement {
  const handleRemoveResponse = (responseId) => {
    if (onRemoveResponse) {
      onRemoveResponse(responseId);
    }
  };

  if (numParticipantsResponded === 0) {
    return (
      <div className="flex items-center justify-center col-span-2 px-4 py-16 border border-solid border-divider rounded-1">
        <span className="font-sans text-sm leading-8 text-grey-600 capitalize">
          {translate('No responses')}
        </span>
      </div>
    );
  }

  const sortedResponses = sortResponses(participants, questionId);

  return (
    <div
      className="grid gap-1 grid-cols-sa-answer p-0"
      data-testid="results-question-answers-SA"
    >
      {sortedResponses.map((response) => {
        return (
          <ResultsQuestionAnswerShortAnswerResponse
            key={response.id}
            participant={response.participant}
            showAnswers={showAnswers}
            handleRemoveResponse={handleRemoveResponse}
            showRemoveAnswer={showRemoveAnswer}
            showNames={showNames}
            response={response}
          />
        );
      })}
    </div>
  );
}
